import type { ActorDatabase } from "@-/dev.improve.haniverse.database.actor";
import {
  cardContentKindDrillKind,
  resolveUserCardContentKind,
  resolveUserCardSubject,
} from "@-/dev.improve.haniverse.lib.cards.card-content";
import { getUserCardStage } from "@-/dev.improve.haniverse.lib.cards.user-card.mastery";
import type {
  LearningStatsStageCardsData,
  ProgressStageKey,
  StageCard,
} from "@-/dev.improve.haniverse.lib.learning-stats.entities";
import { runLearningStatsQueryStep } from "#run-learning-stats-query-step";
import { selectLearnedMeaningStudyCardsByGrapheme } from "#select-learned-meaning-study-cards-by-grapheme";
import { selectLearnedWordStudyCardsByWord } from "#select-learned-word-study-cards-by-word";

function emptyStageCards(): Record<ProgressStageKey, StageCard[]> {
  return { familiar: [], mastered: [], new: [], proficient: [] };
}

/**
 * All active cards, newest activity first, without a lifetime review-history scan. The character
 * and word lists hold only each subject's establishing card, the same card the core slice's
 * `charactersByStage` / `wordsByStage` count, so their lengths match those tallies.
 */
export async function readStageCards(actorDb: ActorDatabase): Promise<LearningStatsStageCardsData> {
  const rows = await runLearningStatsQueryStep("stage cards", () =>
    actorDb(import.meta.location).query.userCard.findMany({
      columns: { deletedAt: true, id: true, manuallyMasteredAt: true, startedStudyingAt: true },
      where: { fsrsCardId: { isNotNull: true } },
      with: {
        fsrsCard: { columns: { due: true, lastReview: true, scheduledDays: true } },
        userGraphemeMeaningCard: { columns: { grapheme: true } },
        userGraphemePronunciationCard: { columns: { grapheme: true, pinyin: true } },
        userWordSentenceCard: { columns: { kind: true, word: true } },
      },
    }),
  );
  const result = {
    cards: emptyStageCards(),
    characters: emptyStageCards(),
    words: emptyStageCards(),
  };
  const activeRows = rows.filter((row) => row.deletedAt === null);
  const characterCardIds = new Set(
    Array.from(selectLearnedMeaningStudyCardsByGrapheme(activeRows).values(), (row) => row.id),
  );
  const wordCardIds = new Set(
    Array.from(selectLearnedWordStudyCardsByWord(activeRows).values(), (row) => row.id),
  );
  const ordered = activeRows.sort((a, b) => {
    const time = (row: typeof a) =>
      row.manuallyMasteredAt ?? row.fsrsCard?.lastReview ?? row.startedStudyingAt ?? "";
    return time(b).localeCompare(time(a)) || a.id.localeCompare(b.id);
  });
  for (const row of ordered) {
    const contentKind = resolveUserCardContentKind(row);
    const subject = resolveUserCardSubject({
      userGraphemeMeaningCard: row.userGraphemeMeaningCard ?? null,
      userGraphemePronunciationCard: row.userGraphemePronunciationCard ?? null,
      userWordSentenceCard: row.userWordSentenceCard ?? null,
    });
    // A card whose overlay has not synced yet has nothing to show on a tile, and a quiz has no
    // glyph to tile (this read never joins quiz overlays).
    if (contentKind === null || contentKind === "quiz" || subject === null) {
      continue;
    }
    const stage = getUserCardStage({
      fsrsCard: row.fsrsCard ?? null,
      manuallyMasteredAt: row.manuallyMasteredAt,
    });
    const card: StageCard = {
      cardType: cardContentKindDrillKind(contentKind),
      due: row.fsrsCard?.due ?? null,
      pinyin: row.userGraphemePronunciationCard?.pinyin ?? null,
      subject: subject.text,
      userCardId: row.id,
    };
    result.cards[stage].push(card);
    if (characterCardIds.has(row.id)) {
      result.characters[stage].push(card);
    }
    if (wordCardIds.has(row.id)) {
      result.words[stage].push(card);
    }
  }
  return result;
}
